import { useFormik } from 'formik';
import { LoadingButton } from '@mui/lab';
import {
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Stack,
  Box,
  Typography,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { bucketValidationSchema } from '../../utils/storageValidation';
import { getAvailableUsers } from '../../utils/storageUtils';

export default function AddBucketDialog({ open, onClose, onSubmit, loading, users }) {
  const formik = useFormik({
    initialValues: {
      name: '',
      path: '',
      assigned_user: '',
    },
    validationSchema: bucketValidationSchema,
    onSubmit: async (values) => {
      const result = await onSubmit(values);
      if (result.success) {
        onClose();
        formik.resetForm();
      }
    },
  });

  const handleClose = () => {
    onClose();
    formik.resetForm();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Bucket</DialogTitle>
      <DialogContent>
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Bucket Name"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />

            <TextField
              fullWidth
              label="Path"
              name="path"
              value={formik.values.path}
              onChange={formik.handleChange}
              error={formik.touched.path && Boolean(formik.errors.path)}
              helperText={formik.touched.path && formik.errors.path}
              placeholder="/storage/drive-name"
            />

            <Autocomplete
              options={getAvailableUsers(users)}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              value={users.find((user) => user.id === formik.values.assigned_user) || null}
              onChange={(event, newValue) => {
                formik.setFieldValue('assigned_user', newValue ? newValue.id : '');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assign User"
                  placeholder="Select a user to assign to this bucket"
                  error={formik.touched.assigned_user && Boolean(formik.errors.assigned_user)}
                  helperText={formik.touched.assigned_user && formik.errors.assigned_user}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box>
                      <Typography variant="body2">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            />
          </Stack>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <LoadingButton
          onClick={formik.handleSubmit}
          loading={loading}
          variant="contained"
          loadingPosition="start"
          startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
        >
          Create Bucket
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
